// Outfit / part toggles: which named objects in a GLTF scene are visible.

// Named objects in hierarchy order. Bones and unnamed nodes aren't listed,
// but anything parented under them (e.g. a prop on a hand bone) still is.
export function collectParts(scene) {
  const parts = [];
  const walk = (obj, depth) => {
    obj.children.forEach((c) => {
      if (c.isLight || c.isCamera) return;
      if (c.isBone || !c.name) { walk(c, depth); return; }
      parts.push({ name: c.name, depth, mesh: !!c.isMesh, visible: c.userData.__origVisible ?? c.visible });
      walk(c, depth + 1);
    });
  };
  walk(scene, 0);
  return parts;
}

// Defaults first, then each active toggle in list order (later toggles win overlaps).
export function computeVisibility(looks, active = []) {
  const vis = { ...(looks?.defaults || {}) };
  (looks?.toggles || []).forEach((t) => {
    if (!active.includes(t.id)) return;
    t.show.forEach((n) => { vis[n] = true; });
    t.hide.forEach((n) => { vis[n] = false; });
  });
  return vis;
}

export function applyLooks(scene, looks, active) {
  const vis = computeVisibility(looks, active);
  scene.traverse((o) => {
    if (o === scene) return;
    if (o.userData.__origVisible === undefined) o.userData.__origVisible = o.visible;
    const v = o.name ? vis[o.name] : undefined;
    o.visible = v === undefined ? o.userData.__origVisible : v;
  });
}

// Drop references to parts that no longer exist in the (possibly replaced) file.
export function pruneLooks(looks, parts) {
  const names = new Set(parts.map((p) => p.name));
  const keep = (list) => (list || []).filter((n) => names.has(n));
  return {
    defaults: Object.fromEntries(Object.entries(looks?.defaults || {}).filter(([k]) => names.has(k))),
    toggles: (looks?.toggles || []).map((t) => ({ ...t, show: keep(t.show), hide: keep(t.hide) }))
  };
}

// Colour / texture swaps from active toggles in list order (later toggles win).
export function computeSwaps(looks, active = []) {
  const out = [];
  (looks?.toggles || []).forEach((t) => {
    if (active.includes(t.id)) (t.swaps || []).forEach((x) => out.push({ material: x.material, color: x.color || null, texture: x.texture || null }));
  });
  return out;
}

// Turning on a toggle in a pick-one group turns off the others in that group.
export function toggleActive(toggles, active, t) {
  if (active.includes(t.id)) return active.filter((x) => x !== t.id);
  const g = (t.group || '').trim();
  const clash = new Set(g ? (toggles || []).filter((o) => o.id !== t.id && (o.group || '').trim() === g).map((o) => o.id) : []);
  return [...active.filter((x) => !clash.has(x)), t.id];
}
